import { forEach } from 'ramda';
import * as THREE from 'three';
import { fetchTextureImage } from '../translators/asset';
import { getUrl } from '../modules/assets';
import path from 'path';

const NearestFilter = 1003;
const NearestMipMapNearestFilter = 1004;
const NearestMipMapLinearFilter = 1005;
const LinearFilter = 1006;
const LinearMipMapNearestFilter = 1007;
const LinearMipMapLinearFilter = 1008;

const Linear = 3000;
const sRGB = 3001;
const RGBE = 3002;
const LogLUV = 3003;
const RGBM7 = 3004;
const RGBM16 = 3005;

const renderType = {
  Original: 0, WebGL: 1, Renderer: 3,
};

const renderTypes = {
  values: [0, 1, 2],
  labels: ['Original', 'WebGL', 'Renderer'],
};

const wrapType = {
  values: [0, 1, 2],
  labels: ['None', 'Wrap', 'Mirror'],
};

const sampleFilter = {
  values: [NearestFilter, NearestMipMapNearestFilter, NearestMipMapLinearFilter, LinearFilter, LinearMipMapNearestFilter, LinearMipMapLinearFilter],
  labels: ['Nearest', 'Nearest MipMap Nearest', 'Nearest MipMap Linear', 'Linear', 'Linear MipMap Nearest', 'Linear MipMap Linear'],
};

const encodingType = {
  values: [Linear, sRGB, RGBE, LogLUV, RGBM7, RGBM16],
  labels: ['Linear', 'sRGB', 'RGBE/Radiance', 'Log LUV', 'RGBM 7', 'RGBM 16'],
};

const Image = {
  schema: {
    originalBitmapFile: { type: 'Image', hidden: true },
    glBitmapFile:       { type: 'Image', hidden: true },
    rendererBitmapFile: { type: 'Image', hidden: true },
    hdrBitmapFile:      { type: 'Image', hidden: true },

    renderOption: { label: 'Server Image', type: 'Options', defaultValue: 0, values: renderTypes.values, labels: renderTypes.labels },
    glOption: { label: 'WebGL Image', type: 'Options', defaultValue: 1, values: renderTypes.values, labels: renderTypes.labels },

    uOffset:  { type: 'Number', label: 'U Offset', defaultValue: 0, step: 0.1, animatable: true },
    vOffset:  { type: 'Number', label: 'V Offset', defaultValue: 0, step: 0.1, animatable: true },
    uTile:    { type: 'Number', label: 'U Tile',   defaultValue: 1, step: 0.1, animatable: true },
    vTile:    { type: 'Number', label: 'V Tile',   defaultValue: 1, step: 0.1, animatable: true },
    rotation: { type: 'Number', label: 'Rotation', defaultValue: 0, step: 0.1, animatable: true },
    wrapU:    { type: 'Options', defaultValue: 1, values: wrapType.values, labels: wrapType.labels, label: 'U Wrap Style' },
    wrapV:    { type: 'Options', defaultValue: 1, values: wrapType.values, labels: wrapType.labels, label: 'V Wrap Style' },

    invert:  { type: 'Boolean', label: 'Invert', defaultValue: false, animatable: true },
    gainPivot:  { type: 'Number', label: 'Gain Pivot', defaultValue: 0, step: 0.1, animatable: true },
    gain:  { type: 'Number', label: 'Gain', defaultValue: 1.0, step: 0.1, animatable: true },
    brightness:    { type: 'Number', label: 'Brightness',  defaultValue: 0, step: 0.1, animatable: true },

    magFilter:    { type: 'Options', defaultValue: LinearFilter, values: sampleFilter.values, labels: sampleFilter.labels, label: 'Magnify Filter' },
    minFilter:    { type: 'Options', defaultValue: LinearMipMapLinearFilter, values: sampleFilter.values, labels: sampleFilter.labels, label: 'Minify Filter' },
    generateMipMaps:    { label: 'Use MipMaps', type: 'Boolean', defaultValue: true },

    hdrEncoding: { type: 'Options', defaultValue: RGBM16, values: encodingType.values, labels: encodingType.labels, label: 'HDR Encoding' },
  },

  update: function (operator, primitive, { node, store }) {
    let asset = null;

    //const renderOption = __SERVER__ ? operator.renderOption : operator.glOption;
    const renderOption = operator.glOption;

    if (renderOption == renderType.Original) {
      asset = operator.originalBitmapFile;
    } else if (renderOption === renderType.WebGL) {
      asset = operator.glBitmapFile || operator.originalBitmapFile;
    } else {
      asset = operator.rendererBitmapFile || operator.originalBitmapFile;
    }

    primitive.asset = asset;
    primitive.hdr = operator.hdrBitmapFile;

    // need reference to original hdr since headless/IE are not handling the hdr.png's properly
    // hdr extension must be 'hdr' - we do not support alternative hdr formats (ex. 'exr')
    const hdrSourceUrl = getUrl(store, operator.originalBitmapFile);
    if (operator.hdrBitmapFile && path.extname(hdrSourceUrl) === '.hdr') primitive.hdrSource = operator.originalBitmapFile;

    forEach((key) => {
      primitive[key] = operator[key];
    }, ['uOffset', 'vOffset', 'uTile', 'vTile', 'rotation', 'wrapU', 'wrapV', 'gain', 'gainPivot', 'brightness', 'magFilter', 'minFilter', 'generateMipMaps', 'hdrEncoding', 'invert']);

    primitive.preferHDR = (operator.hdrEncoding !== Linear) && !!operator.hdrBitmapFile;

    return primitive;
  },
};

const Canvas = {
  schema: {
    width: { type: 'Number', defaultValue: 512, minValue: 16, maxValue: 4096, step: 1, label: 'Width' },
    height: { type: 'Number', defaultValue: 512, minValue: 16, maxValue: 4096, step: 1, label: 'Height' },

    color: { label: 'Color', type: 'Color', defaultValue: { r: 0, g: 0, b: 0, a: 0 }, animatable: true },
    opacity: { label: 'Opacity', type: 'Number', defaultValue: 1, minValue: 0, maxValue: 1, step: 0.1, animatable: true },

    uOffset:  { type: 'Number', label: 'U Offset', defaultValue: 0, step: 0.1, animatable: true },
    vOffset:  { type: 'Number', label: 'V Offset', defaultValue: 0, step: 0.1, animatable: true },
    uTile:    { type: 'Number', label: 'U Tile',   defaultValue: 1, step: 0.1, animatable: true },
    vTile:    { type: 'Number', label: 'V Tile',   defaultValue: 1, step: 0.1, animatable: true },
    rotation: { type: 'Number', label: 'Rotation', defaultValue: 0, step: 0.1, animatable: true },
    wrapU:    { type: 'Options', defaultValue: 1, values: wrapType.values, labels: wrapType.labels, label: 'U Wrap Style' },
    wrapV:    { type: 'Options', defaultValue: 1, values: wrapType.values, labels: wrapType.labels, label: 'V Wrap Style' },

    invert:  { type: 'Boolean', label: 'Invert', defaultValue: false, animatable: true },
    gainPivot:  { type: 'Number', label: 'Gain Pivot', defaultValue: 0, step: 0.1, animatable: true },
    gain:  { type: 'Number', label: 'Gain', defaultValue: 1.0, step: 0.1, animatable: true },
    brightness:    { type: 'Number', label: 'Brightness',  defaultValue: 0, step: 0.1, animatable: true },

    magFilter:    { type: 'Options', defaultValue: THREE.LinearFilter, values: sampleFilter.values, labels: sampleFilter.labels, label: 'Magnify Filter' },
    minFilter:    { type: 'Options', defaultValue: THREE.LinearMipMapLinearFilter, values: sampleFilter.values, labels: sampleFilter.labels, label: 'Minify Filter' },
    generateMipMaps:    { label: 'Use MipMaps', type: 'Boolean', defaultValue: true },

    hdrEncoding: { type: 'Options', defaultValue: THREE.RGBM16Encoding, values: encodingType.values, labels: encodingType.labels, label: 'HDR Encoding' },
  },

  update: function (operator, primitive, { node }) {

    const ctxWidth = operator.width;
    const ctxHeight = operator.height;

    let color = operator.color;
    if (color) color = 'rgba(' + parseInt(color.r * 255) + ',' + parseInt(color.g * 255) + ',' + parseInt(color.b * 255) + ',' + operator.opacity + ')';

    forEach((key) => {
      primitive[key] = operator[key];
    }, ['uOffset', 'vOffset', 'uTile', 'vTile', 'rotation', 'wrapU', 'wrapV', 'gain', 'gainPivot', 'brightness', 'magFilter', 'minFilter', 'generateMipMaps', 'hdrEncoding', 'invert', 'width', 'height']);

    const op = (context) => {
      context.fillStyle = color;
      context.fillRect(0, 0, ctxWidth, ctxHeight);
      return true;
    };

    if (!primitive.canvasOperations) {
      primitive.canvasOperations = [op];
    } else {
      primitive.canvasOperations.push(op);
    }

    return primitive;
  },

};

const CanvasLinearGradient = {
  schema: {
    startIs: { label: 'Start Is', type: 'Options', values: ['top', 'left', 'top-left', 'bottom-left'], initialValue: 'top' },
    startColor: { label: 'Start Color', type: 'Color', defaultValue: { r: 0, g: 0, b: 0, a: 0 }, animatable: true },
    startOpacity: { label: 'Start Opacity', type: 'Number', defaultValue: 1, minValue: 0, maxValue: 1, step: 0.1, animatable: true },
    endColor: { label: 'End Color', type: 'Color', defaultValue: { r: 0, g: 0, b: 0, a: 0 }, animatable: true },
    endOpacity: { label: 'End Opacity', type: 'Number', defaultValue: 1, minValue: 0, maxValue: 1, step: 0.1, animatable: true },

    xOffset: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'X', animatable: true },
    yOffset: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'Y', animatable: true },
    width: { type: 'Number', defaultValue: 512, minValue: 16, maxValue: 4096, step: 1, label: 'Width' },
    height: { type: 'Number', defaultValue: 512, minValue: 16, maxValue: 4096, step: 1, label: 'Height' },

    composition: { label: 'Mode', type: 'Options', values: ['source-over', 'source-in', 'source-out', 'source-atop', 'destination-over', 'destination-in', 'destination-out', 'destination-atop', 'lighter', 'copy', 'xor', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity'], initialValue: 'source-over' },

  },

  update: function (operator, primitive) {
    const xOffset = operator.xOffset;
    const yOffset = operator.yOffset;
    const width = operator.width;
    const height = operator.height;

    let startColor = operator.startColor;
    let endColor = operator.endColor;
    if (startColor) startColor = 'rgba(' + parseInt(startColor.r * 255) + ',' + parseInt(startColor.g * 255) + ',' + parseInt(startColor.b * 255) + ',' + operator.startOpacity + ')';
    if (endColor) endColor = 'rgba(' + parseInt(endColor.r * 255) + ',' + parseInt(endColor.g * 255) + ',' + parseInt(endColor.b * 255) + ',' + operator.endOpacity + ')';

    const op = (canvasContext) => {
      let grd;
      switch (operator.startIs) {
      case 'top':     grd = canvasContext.createLinearGradient(xOffset, yOffset, xOffset, yOffset + height); break;
      case 'left':    grd = canvasContext.createLinearGradient(xOffset, yOffset, xOffset + width, yOffset); break;
      case 'top-left':grd = canvasContext.createLinearGradient(xOffset, yOffset, xOffset + width, yOffset + height); break;
      default:        grd = canvasContext.createLinearGradient(xOffset, yOffset + height, xOffset + width, yOffset); break;
    }
      grd.addColorStop(0, startColor);
      grd.addColorStop(1, endColor);
      canvasContext.fillStyle = grd;

      canvasContext.globalCompositeOperation = operator.composition;

      canvasContext.fillRect(xOffset, yOffset, width, height);
      return true;
    };

    if (!primitive.canvasOperations) {
      primitive.canvasOperations = [op];
    } else {
      primitive.canvasOperations.push(op);
    }

    return primitive;
  },

};

const CanvasFill = {
  schema: {
    color: { label: 'Color', type: 'Color', defaultValue: { r: 0, g: 0, b: 0, a: 0 }, animatable: true },
    opacity: { label: 'Opacity', type: 'Number', defaultValue: 1, minValue: 0, maxValue: 1, step: 0.1, animatable: true },

    xOffset: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'X', animatable: true },
    yOffset: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'Y', animatable: true },
    width: { type: 'Number', defaultValue: 512, minValue: 16, maxValue: 4096, step: 1, label: 'Width' },
    height: { type: 'Number', defaultValue: 512, minValue: 16, maxValue: 4096, step: 1, label: 'Height' },

    composition: { label: 'Mode', type: 'Options', values: ['source-over', 'source-in', 'source-out', 'source-atop', 'destination-over', 'destination-in', 'destination-out', 'destination-atop', 'lighter', 'copy', 'xor', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity'], initialValue: 'source-over' },

  },

  update: function (operator, primitive) {
    const xOffset = operator.xOffset;
    const yOffset = operator.yOffset;
    const width = operator.width;
    const height = operator.height;

    let color = operator.color;
    if (color) color = 'rgba(' + parseInt(color.r * 255) + ',' + parseInt(color.g * 255) + ',' + parseInt(color.b * 255) + ',' + operator.opacity + ')';

    const op = (canvasContext) => {
      canvasContext.fillStyle = color;
      canvasContext.globalCompositeOperation = operator.composition;
      canvasContext.fillRect(xOffset, yOffset, width, height);
      return true;
    };

    if (!primitive.canvasOperations) {
      primitive.canvasOperations = [op];
    } else {
      primitive.canvasOperations.push(op);
    }

    return primitive;
  },
};

const CanvasComposite = {
  schema: {
    sourceImage: { type: 'Plug', plug: 'Image' },
    xOffset: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'X', animatable: true },
    yOffset: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'Y', animatable: true },

    overrideSize: { type: 'Boolean', label: 'Override Default Size', defaultValue: false, animatable: true },
    overrideWidth: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'Override Width', animatable: true },
    overrideHeight: { type: 'Number', defaultValue: 0, minValue: -10000, maxValue: +10000, step: 1, label: 'Override Height', animatable: true },

    composition: { label: 'Composition', type: 'Options', values: ['source-over', 'source-in', 'source-out', 'source-atop', 'destination-over', 'destination-in', 'destination-out', 'destination-atop', 'lighter', 'copy', 'xor', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity'], initialValue: 'source-over' },

  },

  update: function (operator, primitive, { node }) {
    const sourceImage = operator.sourceImage;

    const xOffset = operator.xOffset;
    const yOffset = operator.yOffset;
    const overrideSize = operator.overrideSize;
    let overrideWidth = operator.overrideWidth;
    let overrideHeight = operator.overrideHeight;
    const composition = operator.composition;

    const op = (context, store) => {
      if (!sourceImage) return false;

      const textureImage = fetchTextureImage(store, sourceImage);
      if (!textureImage) {
        return false;
      }

      if (!overrideSize) {
        overrideWidth = textureImage.width;
        overrideHeight = textureImage.height;
      }

      if (overrideWidth == 0) {
        overrideWidth = 1;
      }

      if (overrideHeight == 0) {
        overrideHeight = 1;
      }

      const imgCanvas = document.createElement('canvas');
      imgCanvas.width = overrideWidth;
      imgCanvas.height = overrideHeight;
      const imgCanvasContext = imgCanvas.getContext('2d');

      imgCanvasContext.drawImage(textureImage, 0, 0, overrideWidth, overrideHeight);

      context.globalCompositeOperation = composition;

      context.drawImage(imgCanvas, xOffset, yOffset, overrideWidth, overrideHeight);

      return true;
    };

    if (!primitive.canvasOperations) {
      primitive.canvasOperations = [op];
    } else {
      primitive.canvasOperations.push(op);
    }

    return primitive;
  },
};

const CanvasText = {
  schema: {

    fontName: { label: 'Font Name', type: 'Options', defaultValue:'Serif', values: ['Serif', 'Sans Serif', 'Monoscape'] },
    text:{ label: 'Text', type: 'String', defaultValue: 'Text'},
    fontSize: { label: 'Font Size', type: 'Number', defaultValue: 100, minValue: 1, maxValue: 512, step: 1, animatable: true },
    color: { label: 'Color', type: 'Color', defaultValue: { r: 0, g: 0, b: 0 }, animatable: true },
    opacity: { label: 'Opacity', type: 'Number', defaultValue: 1, minValue: 0, maxValue: 1, step: 0.01, animatable: true  },

    positionX: { type: "Number", defaultValue: 256, minValue: 0, maxValue: 512, step: 1, label: "Position X", animatable: true },
    positionY: { type: "Number", defaultValue: 256, minValue: 0, maxValue: 512, step: 1, label: "Position Y", animatable: true },

    horizontalAlign: { type: "Options", defaultValue: 'center', values: ['left', 'center', 'right'], label: "Horizontal Align" },
    verticalAlign: { type: "Options",  defaultValue: 'middle', values: ['top', 'middle',  'bottom'], label: "Vertical Align" },

  },

  update: function (operator, primitive) {
    var fontName = operator.fontName;
    switch(fontName) {
      case 'Serif':
          fontName = "Times New Roman";
          break;
      case 'Sans Serif':
          fontName = "Arial";
          break;
      case 'Monoscape':
          fontName = "Courier New";
          break;
    }

    var text = operator.text;
    var fontSize = operator.fontSize;

    var color = operator.color;
    var opacity = operator.opacity;
    color = 'rgba(' + parseInt(color.r*255) + ',' + parseInt(color.g*255) + ',' + parseInt(color.b*255) + ',' + opacity+ ')';

    var positionX = operator.positionX;
    var positionY = operator.positionY;
    var horizontalAlign = operator.horizontalAlign;
    var verticalAlign = operator.verticalAlign;

    const op = (canvasContext) => {
      canvasContext.fillStyle = color;
      canvasContext.font = fontSize + 'px ' + fontName;
      canvasContext.textAlign = horizontalAlign;
      canvasContext.textBaseline = verticalAlign;
      canvasContext.fillText(text, positionX, positionY);
      return true;
    };

    if (!primitive.canvasOperations) {
      primitive.canvasOperations = [op];
    } else {
      primitive.canvasOperations.push(op);
    }

    return primitive;
  },
};


export default { Image, Canvas, CanvasLinearGradient, CanvasFill, CanvasComposite, CanvasText };



// WEBPACK FOOTER //
// ./src/operators/Image.js